import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'

interface ColisQRCodeProps {
  trackingCode: string
  contactName?: string
  dropoffAddress?: string
  price?: number
  paymentMethod?: 'cash' | 'online'
  size?: number
  showActions?: boolean
}

export const ColisQRCode = ({
  trackingCode,
  contactName,
  dropoffAddress,
  price,
  paymentMethod,
  size = 200,
  showActions = true,
}: ColisQRCodeProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [dataUrl, setDataUrl] = useState('')
  const [error, setError] = useState('')

  const trackingUrl = `https://traiqgo.tn/track/${trackingCode}`

  useEffect(() => {
    if (!canvasRef.current) return
    QRCode.toCanvas(canvasRef.current, trackingUrl, {
      width: size,
      margin: 2,
      color: { dark: '#0D1117', light: '#FFFFFF' },
      errorCorrectionLevel: 'H',
    }, (err) => {
      if (err) { setError('Erreur lors de la génération du QR code'); return }
      setDataUrl(canvasRef.current!.toDataURL('image/png'))
    })
  }, [trackingCode, size, trackingUrl])

  const handleDownload = () => {
    if (!dataUrl) return
    const a = document.createElement('a')
    a.href = dataUrl
    a.download = `qr-${trackingCode}.png`
    a.click()
  }

  const handlePrint = () => {
    const win = window.open('', '_blank')
    if (!win) return
    win.document.write(`<!DOCTYPE html><html><head><title>QR Code — ${trackingCode}</title><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:Arial,sans-serif;display:flex;flex-direction:column;align-items:center;
        justify-content:center;min-height:100vh;padding:32px;background:#fff}
      .label{font-size:11px;color:#666;text-transform:uppercase;letter-spacing:.08em;margin-bottom:4px}
      .tracking{font-size:22px;font-weight:700;color:#0D1117;letter-spacing:.05em;margin-bottom:16px}
      .qr-wrapper{border:2px solid #E5E7EB;border-radius:12px;padding:16px;margin-bottom:20px;display:inline-block}
      .meta{font-size:13px;color:#374151;margin-bottom:6px;text-align:center}
      .meta strong{color:#0D1117}
      .footer{margin-top:16px;font-size:11px;color:#9CA3AF}
      @media print{body{padding:16px}}
    </style></head><body>
      <div class="label">Colis TariqGo</div>
      <div class="tracking">${trackingCode}</div>
      <div class="qr-wrapper"><img src="${dataUrl}" width="${size}" height="${size}" alt="QR Code ${trackingCode}" /></div>
      ${contactName    ? `<div class="meta">Destinataire : <strong>${contactName}</strong></div>` : ''}
      ${dropoffAddress ? `<div class="meta">Adresse : <strong>${dropoffAddress}</strong></div>` : ''}
      ${price !== undefined ? `<div class="meta">Prix : <strong>${price} TND</strong> · ${paymentMethod === 'cash' ? 'Cash' : 'En ligne'}</div>` : ''}
      <div class="footer">Scanner pour suivre ce colis · traiqgo.tn</div>
    </body></html>`)
    win.document.close()
    win.focus()
    win.onload = () => { win.print(); win.close() }
  }

  if (error) return <div style={{ color: '#dc2626', fontSize: 13, textAlign: 'center' }}>{error}</div>

  const G = '#1D9E75'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 16, display: 'inline-block', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb' }}>
        <canvas ref={canvasRef} style={{ display: 'block', borderRadius: 8 }} />
      </div>

      <div style={{ textAlign: 'center' }}>
        <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Code de suivi
        </div>
        <div style={{ color: G, fontSize: 18, fontWeight: 700, letterSpacing: '0.06em', fontFamily: 'monospace' }}>
          {trackingCode}
        </div>
        <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 4 }}>
          {trackingUrl}
        </div>
      </div>

      {showActions && (
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleDownload}
            disabled={!dataUrl}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#fff', border: '1px solid #e5e7eb',
              borderRadius: 10, color: '#374151',
              fontSize: 13, fontWeight: 500,
              padding: '9px 16px', cursor: dataUrl ? 'pointer' : 'not-allowed',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Télécharger PNG
          </button>

          <button
            onClick={handlePrint}
            disabled={!dataUrl}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: G, border: 'none',
              borderRadius: 10, color: '#fff',
              fontSize: 13, fontWeight: 600,
              padding: '9px 16px', cursor: dataUrl ? 'pointer' : 'not-allowed',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Imprimer
          </button>
        </div>
      )}
    </div>
  )
}
